import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut, Radar, Line, Pie } from 'react-chartjs-2';
import BrandAffinityRenderer from './BrandAffinityRenderer';
import PersonalValuesRenderer from './PersonalValuesRenderer';
import AudienceRenderer from './AudienceRenderer';
import EducationRenderer from './EducationRenderer';
import EmploymentStatusRenderer from './EmploymentStatusRenderer';
import SiteAffinityRenderer from './SiteAffinityRenderer';
import AeoGeoSeoRenderer from './AeoGeoSeoRenderer';
import PsychologicalDriversChart from './PsychologicalDriversChart';
import HobbiesHabitsChart from './HobbiesHabitsChart';
import DailyRoutineChart from './DailyRoutineChart';
import useReveal from '../../hooks/useReveal';
import useChartReveal from '../../hooks/useChartReveal';
import { BAR_ANIMATION_MS, REVEAL_DELAY_MS, REVEAL_DURATION_MS, toMs } from '../../constants/animation';

ChartJS.register(
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

type Primitive = string | number | boolean | null;

interface GenericSectionRendererProps {
  title: string;
  data: any;
  primaryColor?: string;
  secondaryColor?: string;
}

const AnimatedBar: React.FC<{ data: any; options: any; isVisible: boolean }> = ({ data, options, isVisible }) => {
  const chartRef = useChartReveal(isVisible);
  return (
    <Bar
      ref={chartRef}
      data={data}
      options={{
        ...options,
        animation: {
          duration: isVisible ? BAR_ANIMATION_MS : 0,
          easing: 'easeOutCubic',
        },
      }}
    />
  );
};

/**
 * Attempts to render unknown AI section data.
 * Priority:
 * 1) If data is an object of subgroups (each subgroup is an array of label/value or a key:number map)
 *    → render a grid of charts (one per subgroup)
 * 2) If data is a label/value array → bar chart
 * 3) If data is a key:number object → bar chart
 * 4) Fallback → table
 */
export default function GenericSectionRenderer({ title, data, primaryColor = '#14B8A6', secondaryColor = '#FDBA19' }: GenericSectionRendererProps) {
  const { ref, isVisible } = useReveal<HTMLDivElement>({ threshold: 0.2, once: true });
  const cardRevealStyle = (offset = 0) => {
    const delay = isVisible ? REVEAL_DELAY_MS + offset : 0;
    return {
      transition: `transform ${toMs(REVEAL_DURATION_MS)} ease-out, opacity ${toMs(REVEAL_DURATION_MS)} ease-out`,
      transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
      opacity: isVisible ? 1 : 0.25,
      transitionDelay: toMs(delay),
    };
  };

  // Special handling for Education
  if (title.toLowerCase().includes('education')) {
    return <EducationRenderer title={title} data={data} primaryColor={primaryColor} />;
  }
  
  // Special handling for Employment Status
  if (title.toLowerCase().includes('employment') || title.toLowerCase().includes('employment status')) {
    return <EmploymentStatusRenderer title={title} data={data} primaryColor={primaryColor} />;
  }
  
  // Special handling for Audience (and Demographic Highlights styled as Audience)
  if (title.toLowerCase().includes('audience') ||
      title.toLowerCase().includes('demographic highlights') ||
      (data && typeof data === 'object' &&
       (data.gender || data.Gender || data.age || data.ageGroup || data['Age Group']))) {
    const normalizedTitle = title.toLowerCase().includes('demographic highlights') ? 'Audience' : title;
    let normalizedData: any = data;
    if (Array.isArray(data)) {
      // Convert array of subgroup objects into a single object { gender, age, education, employment }
      const obj: any = {};
      for (const item of data as any[]) {
        const t = String(item?.title || '').toLowerCase();
        const d = item?.data;
        if (!d) continue;
        if (t.includes('gender')) obj.gender = d;
        if (t.includes('age')) obj.age = d;
        if (t.includes('education')) obj.education = d;
        if (t.includes('employment')) obj.employment = d;
      }
      normalizedData = obj;
    }
    return <AudienceRenderer title={normalizedTitle} data={normalizedData} primaryColor={primaryColor} />;
  }
  
  // Special handling for Personal Values
  if (title.toLowerCase().includes('personal values') || title.toLowerCase().includes('values')) {
    return <PersonalValuesRenderer title={title} data={data} primaryColor={primaryColor} />;
  }

  // Psychological Drivers
  if (title.toLowerCase().includes('psychological driver')) {
    return <PsychologicalDriversChart data={Array.isArray(data) ? data : []} primaryColor={primaryColor} secondaryColor={secondaryColor} />;
  }

  // Hobbies & Habits
  if (title.toLowerCase().includes('hobbies') || title.toLowerCase().includes('habits')) {
    return <HobbiesHabitsChart data={Array.isArray(data) ? data : []} primaryColor={primaryColor} secondaryColor={secondaryColor} />;
  }

  // Daily Routine
  if (title.toLowerCase().includes('daily routine') || title.toLowerCase().includes('routine')) {
    return <DailyRoutineChart data={Array.isArray(data) ? data : []} primaryColor={primaryColor} secondaryColor={secondaryColor} />;
  }

  // Special handling for AEO/GEO/SEO
  if (title.toLowerCase().includes('aeo/geo/seo') || title.toLowerCase().includes('aeo') || title.toLowerCase().includes('geo') || title.toLowerCase().includes('seo')) {
    return <AeoGeoSeoRenderer title={title} data={data} primaryColor={primaryColor} secondaryColor={secondaryColor} />;
  }
  
  // Special handling for Site Affinity
  if (title.toLowerCase().includes('site affinity') || title.toLowerCase().includes('site index') || title.toLowerCase().includes('publisher')) {
    return <SiteAffinityRenderer title={title} data={data} primaryColor={primaryColor} />;
  }

  // Category Affinity removed per request

  // Special handling for Brand Affinity (after site/category to avoid mis-routing on the word "affinity")
  if (title.toLowerCase().includes('brand') || title.toLowerCase().includes('brand affinity') || 
      (data && typeof data === 'object' && Object.keys(data).some(k => 
        k.toLowerCase().includes('retailer') || k.toLowerCase().includes('restaurant') || 
        k.toLowerCase().includes('hotel') || k.toLowerCase().includes('airline')
      ))) {
    return <BrandAffinityRenderer title={title} data={data} primaryColor={primaryColor} />;
  }
  const asArray = Array.isArray(data) ? data : null;
  const first = asArray?.[0];

  const looksLikeLabelValueArray = Array.isArray(asArray) && first && typeof first === 'object' && (
    ('label' in first && typeof first.label === 'string' && 'value' in first && typeof first.value === 'number') ||
    ('name' in first && typeof (first as any).name === 'string' && ('index' in first || 'composition' in first))
  );

  const looksLikeNumberMap = !Array.isArray(data) && data && typeof data === 'object' &&
    Object.values(data).every((v: any) => typeof v === 'number');

  const parseNumber = (v: any): number | null => {
    if (v === null || v === undefined) return null;
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    if (typeof v === 'string') {
      if (/no data/i.test(v)) return null;
      const m = v.match(/[-+]?[0-9]*\.?[0-9]+/);
      if (m) return Number(m[0]);
    }
    return null;
  };

  const looksLikeNestedGroups = !Array.isArray(data) && data && typeof data === 'object' &&
    Object.values(data).some((v: any) => {
      if (Array.isArray(v)) {
        const f = v[0];
        return f && typeof f === 'object' && (('label' in f || 'name' in f) && ('value' in f || 'index' in f || 'composition' in f));
      }
      if (v && typeof v === 'object') {
        return Object.values(v).some((x: any) => typeof parseNumber(x) === 'number');
      }
      return false;
    });

  const chooseType = (t: string, labels: string[]) => {
    const s = (t || '').toLowerCase();
    if (isLikelyTimeSeries(labels)) return 'line';
    if (s.includes('personal values') || s.includes('values')) return 'radar';
    if (s.includes('gender')) return 'doughnut';
    if (s.includes('employment') || s.includes('income') || s.includes('retail') || s.includes('restaurants') || s.includes('top ') || s.includes('airlines') || s.includes('hotels')) return 'hbar';
    if (s.includes('marital') || s.includes('status')) return 'hbar';
    if (s.includes('children')) return 'bar';
    if (labels.length <= 3) return 'doughnut';
    if (labels.length <= 5) return 'pie';
    if (labels.length > 6) return 'hbar';
    return 'bar';
  };

  const MONTHS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','sept','oct','nov','dec'];
  const isLikelyTimeSeries = (labels: string[]) => {
    if (labels.length < 3) return false;
    const ls = labels.map(l => String(l).toLowerCase().trim());
    const isAllYears = ls.every(l => /^\d{4}$/.test(l));
    if (isAllYears) return true;
    const monthHits = ls.filter(l => MONTHS.some(m => l.startsWith(m))).length;
    if (monthHits >= Math.max(3, Math.floor(ls.length * 0.6))) return true;
    const dateLike = ls.filter(l => /\b\d{1,2}[\/\-]\d{1,2}(?:[\/\-]\d{2,4})?\b/.test(l)).length;
    if (dateLike >= Math.max(3, Math.floor(ls.length * 0.6))) return true;
    const numericSeq = ls.every(l => /^-?\d+(?:\.\d+)?$/.test(l));
    return numericSeq;
  };

  const colorPalette = (n: number, base: string) => {
    const fallbacks = [base, '#06B6D4', '#8B5CF6', '#F59E0B', '#EF4444', '#10B981', '#3B82F6', '#A855F7', '#F97316'];
    const arr: string[] = [];
    for (let i = 0; i < n; i++) arr.push(fallbacks[i % fallbacks.length]);
    return arr;
  };

  const emojiFor = (t: string) => {
    const s = t.toLowerCase();
    if (s.includes('gender')) return '⚥ ';
    if (s.includes('age')) return '🎂 ';
    if (s.includes('income')) return '💵 ';
    if (s.includes('employment')) return '💼 ';
    if (s.includes('education')) return '🎓 ';
    if (s.includes('marital')) return '💍 ';
    if (s.includes('children')) return '👶 ';
    if (s.includes('values')) return '🧭 ';
    if (s.includes('retail') || s.includes('brand')) return '🛍️ ';
    return '';
  };
  if (looksLikeNestedGroups) {
    const entries = Object.entries(data);
    return (
      <div ref={ref} className="bg-white rounded-lg shadow-md p-6" style={cardRevealStyle()}>
        <h3 className="text-xl font-bold mb-4">{title}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {entries.map(([groupTitle, groupData], idx) => {
            // Convert each subgroup to a chart via recursion of the heuristics
            // Array of label/value-like
            if (Array.isArray(groupData)) {
              const items = (groupData as any[])
                .map(it => ({
                  label: it.label ?? it.name ?? String(it.title ?? ''),
                  value: parseNumber(it.value ?? it.index ?? it.composition)
                }))
                .filter(it => typeof it.value === 'number');
              if (items.length === 0) return null;
              const labels = items.map(i => i.label);
              const values = items.map(i => i.value as number);
              const chartType = chooseType(String(groupTitle), labels);
              if (chartType === 'doughnut') {
                const doughnutData = { labels, datasets: [{ data: values, backgroundColor: colorPalette(values.length, primaryColor) }] };
                const opts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right' as const } } };
                return (
                  <div key={idx} className="h-72">
                    <div className="text-sm font-semibold text-gray-800 mb-2">{emojiFor(String(groupTitle))}{groupTitle}</div>
                    <Doughnut data={doughnutData} options={opts} />
                  </div>
                );
              }
              if (chartType === 'pie') {
                const pieData = { labels, datasets: [{ data: values, backgroundColor: colorPalette(values.length, primaryColor) }] };
                const opts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right' as const } } };
                return (
                  <div key={idx} className="h-72">
                    <div className="text-sm font-semibold text-gray-800 mb-2">{emojiFor(String(groupTitle))}{groupTitle}</div>
                    <Pie data={pieData} options={opts} />
                  </div>
                );
              }
              if (chartType === 'radar') {
                const radarData = { labels, datasets: [{ label: String(groupTitle), data: values, backgroundColor: 'rgba(20,184,166,0.2)', borderColor: primaryColor, pointBackgroundColor: primaryColor }] };
                const opts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { r: { beginAtZero: true, ticks: { display: false }, grid: { color: '#E5E7EB' }, pointLabels: { color: '#374151' } } } } as const;
                return (
                  <div key={idx} className="h-96">
                    <div className="text-sm font-semibold text-gray-800 mb-2">{emojiFor(String(groupTitle))}{groupTitle}</div>
                    <Radar data={radarData} options={opts} />
                  </div>
                );
              }
              if (chartType === 'line') {
                const lineData = { labels, datasets: [{ label: String(groupTitle), data: values, borderColor: primaryColor, backgroundColor: 'rgba(20,184,166,0.15)', fill: true, tension: 0.3, pointRadius: 2 }] };
                const lineOpts: any = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { ticks: { color: '#6B7280' } }, y: { ticks: { color: '#6B7280' }, beginAtZero: true } } };
                return (
                  <div key={idx} className="h-72">
                    <div className="text-sm font-semibold text-gray-800 mb-2">{emojiFor(String(groupTitle))}{groupTitle}</div>
                    <Line data={lineData} options={lineOpts} />
                  </div>
                );
              }
              const horizontal = chartType === 'hbar';
              const barData = { labels, datasets: [{ label: String(groupTitle), data: values, backgroundColor: primaryColor, borderRadius: 4 }] };
              const barOpts: any = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { ticks: { color: '#6B7280' } }, y: { ticks: { color: '#6B7280' }, beginAtZero: true } }, indexAxis: horizontal ? 'y' : 'x' };
              return (
                <div
                  key={idx}
                  className="h-72"
                  style={cardRevealStyle(idx * 140)}
                >
                  <div className="text-sm font-semibold text-gray-800 mb-2">{emojiFor(String(groupTitle))}{groupTitle}</div>
                  <AnimatedBar data={barData} options={barOpts} isVisible={isVisible} />
                </div>
              );
            }
            // Object of numbers (or numeric strings)
            if (groupData && typeof groupData === 'object') {
              const pairs = Object.entries(groupData as any).map(([k, v]) => ({ label: k, value: parseNumber(v) })).filter(x => typeof x.value === 'number');
              if (pairs.length === 0) return null;
              const labels = pairs.map(p => p.label);
              const values = pairs.map(p => p.value as number);
              const chartType = chooseType(String(groupTitle), labels);
              if (chartType === 'pie') {
                const pieData = { labels, datasets: [{ data: values, backgroundColor: colorPalette(values.length, primaryColor) }] };
                const opts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right' as const } } };
                return (
                  <div key={idx} className="h-72">
                    <div className="text-sm font-semibold text-gray-800 mb-2">{emojiFor(String(groupTitle))}{groupTitle}</div>
                    <Pie data={pieData} options={opts} />
                  </div>
                );
              }
              if (chartType === 'line') {
                const lineData = { labels, datasets: [{ label: String(groupTitle), data: values, borderColor: primaryColor, backgroundColor: 'rgba(20,184,166,0.15)', fill: true, tension: 0.3, pointRadius: 2 }] };
                const lineOpts: any = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { ticks: { color: '#6B7280' } }, y: { ticks: { color: '#6B7280' }, beginAtZero: true } } };
                return (
                  <div key={idx} className="h-72">
                    <div className="text-sm font-semibold text-gray-800 mb-2">{emojiFor(String(groupTitle))}{groupTitle}</div>
                    <Line data={lineData} options={lineOpts} />
                  </div>
                );
              }
              const horizontal = chartType === 'hbar' || labels.length > 6;
              const barData = { labels, datasets: [{ label: String(groupTitle), data: values, backgroundColor: primaryColor, borderRadius: 4 }] };
              const barOpts: any = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { ticks: { color: '#6B7280' } }, y: { ticks: { color: '#6B7280' }, beginAtZero: true } }, indexAxis: horizontal ? 'y' : 'x' };
              return (
                <div key={idx} className="h-72" style={cardRevealStyle(idx * 140)}>
                  <div className="text-sm font-semibold text-gray-800 mb-2">{emojiFor(String(groupTitle))}{groupTitle}</div>
                  <AnimatedBar data={barData} options={barOpts} isVisible={isVisible} />
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>
    );
  }

  if (looksLikeLabelValueArray) {
    const items = asArray!.map((it: any) => ({
      label: it.label ?? it.name ?? String(it.title ?? ''),
      value: typeof it.value === 'number' ? it.value : (typeof it.index === 'number' ? it.index : (typeof it.composition === 'number' ? it.composition : 0)),
      indexValue: typeof it.index === 'number' ? it.index : parseNumber(it.index),
      compositionValue: typeof it.composition === 'number' ? it.composition : parseNumber(it.composition)
    }));

    const labels = items.map(i => i.label);
    const values = items.map(i => i.value);

    // If this looks like a composition that sums to ~100, prefer pie/doughnut
    const sum = values.reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0);
    const isComposition = sum > 95 && sum < 105;

    // Explicit title-driven chart selection to match reference layouts
    const t = title.toLowerCase();
    if (t.includes('gender')) {
      const doughnutData = { labels, datasets: [{ data: values, backgroundColor: colorPalette(values.length, primaryColor) }] };
      const opts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right' as const } } };
      return (
        <div ref={ref} className="bg-white rounded-lg shadow-md p-6" style={cardRevealStyle()}>
          <h3 className="text-xl font-bold mb-4">{title}</h3>
          <div className="h-72"><Doughnut data={doughnutData} options={opts} /></div>
        </div>
      );
    }
    if (t.includes('age')) {
      const barData = { labels, datasets: [{ label: title, data: values, backgroundColor: primaryColor, borderRadius: 4 }] };
      const barOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { ticks: { color: '#6B7280' } }, y: { ticks: { color: '#6B7280' }, beginAtZero: true } } } as const;
      return (
        <div ref={ref} className="bg-white rounded-lg shadow-md p-6" style={cardRevealStyle()}>
          <h3 className="text-xl font-bold mb-4">{title}</h3>
          <div className="h-72"><AnimatedBar data={barData} options={barOpts} isVisible={isVisible} /></div>
        </div>
      );
    }
    if (t.includes('education')) {
      const barData = { labels, datasets: [{ label: title, data: values, backgroundColor: primaryColor, borderRadius: 4 }] };
      const barOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { ticks: { color: '#6B7280' } }, y: { ticks: { color: '#6B7280' }, beginAtZero: true } } } as const;
      return (
        <div ref={ref} className="bg-white rounded-lg shadow-md p-6" style={cardRevealStyle()}>
          <h3 className="text-xl font-bold mb-4">{title}</h3>
          <div className="h-72"><AnimatedBar data={barData} options={barOpts} isVisible={isVisible} /></div>
        </div>
      );
    }
    if (t.includes('employment')) {
      // If single highlighted status, render a meter-style card like the reference
      if (items.length === 1) {
        const only = items[0];
        const idxVal = (only.indexValue ?? only.value) || 0;
        const compVal = only.compositionValue ?? null;
        const pct = Math.max(0, Math.min(100, Number(compVal ?? 0)));
        return (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold mb-1">{title}</h3>
            <div className="text-xs text-gray-500 mb-4">BY INDEX</div>
            <div className="text-teal-600 font-extrabold tracking-wide mb-2">{only.label.toUpperCase()}</div>
            <div className="flex items-center gap-6 mb-4">
              <div className="bg-teal-50 text-gray-900 rounded-md px-3 py-2 text-center">
                <div className="text-xs text-gray-600">INDEX</div>
                <div className="text-2xl font-bold">{idxVal}</div>
              </div>
              {compVal != null && (
                <div className="text-right">
                  <div className="text-xs text-gray-600">% COMPOSITION</div>
                  <div className="text-2xl font-extrabold text-teal-600">{pct}%</div>
                </div>
              )}
            </div>
            <div className="w-full h-6 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-teal-600" style={{ width: `${Math.min(100, idxVal)}%` }} />
            </div>
            <div className="text-center mt-2 text-xs text-gray-400">100<br/>Online Adult Population</div>
          </div>
        );
      }
      // Otherwise fall back to horizontal bars
      const barData = { labels, datasets: [{ label: title, data: values, backgroundColor: primaryColor, borderRadius: 999 }] };
      const barOpts: any = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { ticks: { color: '#6B7280' } }, y: { ticks: { color: '#6B7280' }, beginAtZero: true } }, indexAxis: 'y' };
      return (
        <div ref={ref} className="bg-white rounded-lg shadow-md p-6" style={cardRevealStyle()}>
          <h3 className="text-xl font-bold mb-4">{title}</h3>
          <div className="h-72"><AnimatedBar data={barData} options={barOpts} isVisible={isVisible} /></div>
        </div>
      );
    }
    if (t.includes('top ') || t.includes('retailer') || t.includes('restaurant') || t.includes('hotel') || t.includes('airline') || t.includes('auto')) {
      const barData = { labels, datasets: [{ label: title, data: values, backgroundColor: primaryColor, borderRadius: 999 }] };
      const barOpts: any = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { ticks: { color: '#6B7280' } }, y: { ticks: { color: '#6B7280' }, beginAtZero: true } }, indexAxis: 'y' };
      return (
        <div ref={ref} className="bg-white rounded-lg shadow-md p-6" style={cardRevealStyle()}>
          <h3 className="text-xl font-bold mb-4">{title}</h3>
          <div className="h-96"><AnimatedBar data={barData} options={barOpts} isVisible={isVisible} /></div>
        </div>
      );
    }

    // Heuristics for single-array sections
    const isTwoSlice = values.length <= 3 || isComposition;
    const labelsLower = labels.map(l => l.toLowerCase());
    const isPersonalValues = labelsLower.some(l => ['nature','creativity','stimulation','independence','pleasure','equality','reputation','achievement','authority','influence','safety','tradition','conformity','caring','tolerance','dependability'].includes(l));
    if (isTwoSlice) {
      const doughnutData = { labels, datasets: [{ data: values, backgroundColor: colorPalette(values.length, primaryColor) }] };
      const opts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right' as const } } };
      return (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold mb-4">{title}</h3>
          <div className="h-72"><Doughnut data={doughnutData} options={opts} /></div>
        </div>
      );
    }
    if (isPersonalValues || (/personal values|values/.test(title.toLowerCase()) && labels.length >= 6)) {
      // Compute top 3
      const ranked = items
        .map((it, i) => ({ ...it, i }))
        .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
        .slice(0, 3);
      const topIndexes = new Set(ranked.map(r => r.i));

      const pointColors = values.map((_, i) => (topIndexes.has(i) ? primaryColor : '#94A3B8'));
      const pointRadius = values.map((_, i) => (topIndexes.has(i) ? 5 : 2));

      const radarData = { labels, datasets: [{ label: title, data: values, backgroundColor: 'rgba(20,184,166,0.15)', borderColor: primaryColor, pointBackgroundColor: pointColors, pointRadius }] } as any;
      const opts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { r: { beginAtZero: true, ticks: { display: false }, grid: { color: '#E5E7EB' }, pointLabels: { color: '#374151' } } } } as const;

      const descriptions: Record<string, string> = {
        Nature: 'Emphasize preservation of the environment and sustainability.',
        Creativity: 'Themes of innovation, originality, and imagination resonate.',
        Stimulation: 'Dynamic, adventurous messaging captures attention.',
        Independence: 'Freedom of choice and self‑reliance matter.',
        Pleasure: 'Enjoyment and positive experiences are motivating.',
        Equality: 'Fairness and equal treatment themes resonate.',
        Reputation: 'Public image and being a good citizen matter.',
        Achievement: 'Success and recognition are motivating.',
        Authority: 'Control, influence, and leadership are valued.',
        Influence: 'Persuasion and impact on others are meaningful.',
        Safety: 'Security, harmony, and stability are priorities.',
        Tradition: 'Familiar, time‑tested approaches are preferred.',
        Conformity: 'Restraint and social expectations are important.',
        Caring: 'Community and helping others resonate.',
        Tolerance: 'Understanding and acceptance of different viewpoints.',
        Dependability: 'Reliability and trustworthiness are key.',
      };

      return (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold mb-6">{title}</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <div className="h-[420px]">
              <Radar data={radarData} options={opts} />
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Top 3 Personal Values</h4>
              <div className="space-y-5">
                {ranked.map((r, idx) => (
                  <div key={r.label} className="flex">
                    <div className="text-3xl font-bold text-teal-600 w-16">{r.value}</div>
                    <div>
                      <div className="font-semibold">{idx + 1}. {r.label}</div>
                      <div className="text-sm text-gray-600">{descriptions[r.label] || 'A core personal value that influences behavior.'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }
    const barData = { labels, datasets: [{ label: title, data: values, backgroundColor: primaryColor, borderRadius: 4 }] };
    const barOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { ticks: { color: '#6B7280' } }, y: { ticks: { color: '#6B7280' }, beginAtZero: true } } } as const;
    return (
      <div ref={ref} className="bg-white rounded-lg shadow-md p-6" style={cardRevealStyle()}>
        <h3 className="text-xl font-bold mb-4">{title}</h3>
        <div className="h-72"><AnimatedBar data={barData} options={barOpts} isVisible={isVisible} /></div>
      </div>
    );
  }

  if (looksLikeNumberMap) {
    const labels = Object.keys(data);
    const values = Object.values(data);
    const chartData = {
      labels,
      datasets: [
        {
          label: title,
          data: values,
          backgroundColor: primaryColor,
          borderRadius: 4,
        },
      ],
    };
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: { ticks: { color: '#6B7280' } },
        y: { ticks: { color: '#6B7280' }, beginAtZero: true },
      },
    } as const;
    return (
      <div ref={ref} className="bg-white rounded-lg shadow-md p-6" style={cardRevealStyle()}>
        <h3 className="text-xl font-bold mb-4">{title}</h3>
        <div className="h-72 overflow-hidden">
          <AnimatedBar data={chartData} options={options} isVisible={isVisible} />
        </div>
      </div>
    );
  }

  // Fallback: table
  const renderRow = (k: string, v: Primitive | object, i: number) => (
    <tr key={i} className="border-b">
      <td className="px-3 py-2 text-sm text-gray-700 align-top w-48">{k}</td>
      <td className="px-3 py-2 text-sm text-gray-900">
        {typeof v === 'object' && v !== null ? <pre className="text-xs text-gray-600 overflow-auto">{JSON.stringify(v, null, 2)}</pre> : String(v)}
      </td>
    </tr>
  );

  const rows = Array.isArray(data)
    ? data.map((item: any, idx: number) => renderRow(String(item?.label ?? item?.name ?? `Row ${idx + 1}`), item?.value ?? item, idx))
    : Object.entries(data || {}).map(([k, v], i) => renderRow(k, v as any, i));

  return (
    <div ref={ref} className="bg-white rounded-lg shadow-md p-6" style={cardRevealStyle()}>
      <h3 className="text-xl font-bold mb-4">{title}</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <tbody>{rows}</tbody>
        </table>
      </div>
    </div>
  );
}

// touched for push

// touch
